import {
  EditOutlined,
  LeftOutlined,
  RightOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { Button, Input, Layout, PageHeader, Table } from 'antd';
import React, { useContext, useEffect, useState } from 'react';

import { UserContext } from 'app/context/UserContext/UserContext';
import { useCustomTags } from 'app/hooks/data/useCustomTags';
import type { CustomTag } from 'app/typings/customTags';
import { dayjs } from 'app/utils/dayjs';
import { scrollToTop } from 'app/utils/scroll';
import type { SortOrder } from 'app/utils/sort';
import {
  convertFromAntdSortOrder,
  convertToAntdSortOrder,
} from 'app/utils/sort';
import { sortAndFilterCustomTags } from 'app/utils/sortAndFilter';

import { CustomTagModal } from './CustomTagModal/CustomTagModal';
import './CustomTags.scss';

type CustomTagsQueryParam = {
  search: string;
  pageNumber: number;
  dateSorting: SortOrder;
};

const { Content } = Layout;
const { Column } = Table;

const PAGE_SIZE = 50;

const formatDate = (date: string | Date | null | undefined) => {
  return date && dayjs.utc(date).local().format('DD/MM/YYYY - HH:mm');
};

export type CustomTagsFilter = {
  search?: string;
  dateSorting: SortOrder;
  offset: number;
  limit: number;
};

const makePayload = (queryParam: CustomTagsQueryParam): CustomTagsFilter => {
  return {
    search: queryParam.search,
    dateSorting: queryParam.dateSorting,
    offset: ((queryParam.pageNumber || 1) - 1) * PAGE_SIZE,
    limit: PAGE_SIZE,
  };
};

export type CustomTagForm = {
  id?: number;
  name: string;
};

const EMPTY_CUSTOM_TAG: CustomTagForm = {
  name: '',
};

export const CustomTags = () => {
  const { data: customTags, isLoading } = useCustomTags();
  const { isSuperAdmin } = useContext(UserContext);
  const [filteredTags, setFilteredTags] = useState<CustomTag[]>([]);
  const [selectedTag, setSelectedTag] = useState<CustomTagForm | undefined>(
    undefined
  );
  const [queryParam, updateQueryParam] = useState<CustomTagsQueryParam>({
    pageNumber: 1,
    dateSorting: 'descend',
    search: '',
  });

  useEffect(() => {
    setFilteredTags(
      sortAndFilterCustomTags(customTags || [], makePayload(queryParam))
    );
  }, [queryParam, customTags]);

  const handleOffset = (x: number) => {
    updateQueryParam((prev) => ({
      ...prev,
      pageNumber: (queryParam?.pageNumber || 1) + x,
    }));

    scrollToTop();
  };

  const dateSortingHandler = (sort: SortOrder) => {
    updateQueryParam((prev) => ({
      ...prev,
      pageNumber: 1,
      dateSorting: sort,
    }));
  };

  const handleSearch = (search: string) => {
    updateQueryParam((prev) => ({
      ...prev,
      pageNumber: 1,
      search: search,
    }));
  };

  const pagination = (className: string) => (
    <div className={className}>
      <Button
        disabled={queryParam.pageNumber === 1}
        onClick={() => handleOffset(-1)}
      >
        <LeftOutlined />
      </Button>
      <Button>{queryParam.pageNumber}</Button>
      <Button
        disabled={filteredTags.length < 50}
        onClick={() => handleOffset(1)}
      >
        <RightOutlined />
      </Button>
    </div>
  );

  const header = () => (
    <div className="body-header">
      <div className="left">
        <Input
          placeholder="Search..."
          value={queryParam.search}
          onChange={(event) => handleSearch(event.target.value)}
          suffix={<SearchOutlined />}
          className="search"
          size="large"
        />
      </div>
      {pagination('button-wrapper')}
    </div>
  );

  const newCustomTagButton = (
    <Button
      type="primary"
      size="large"
      onClick={() => setSelectedTag(EMPTY_CUSTOM_TAG)}
    >
      New
    </Button>
  );

  return (
    <Layout className="custom-tag-page-list">
      <PageHeader
        className="header"
        title="Custom tags"
        extra={isSuperAdmin ? newCustomTagButton : undefined}
      />
      <Content className="body">
        <Table
          dataSource={filteredTags}
          pagination={false}
          title={header}
          loading={isLoading}
          rowKey="id"
          onChange={(__, ___, sorter) => {
            // @ts-ignore -- legacy directive, needs investigation
            const { order } = sorter;

            dateSortingHandler(convertFromAntdSortOrder(order));
          }}
        >
          <Column title="Name" dataIndex="name" width={608} />
          <Column
            title="Creation date"
            dataIndex="createdAt"
            sorter
            sortOrder={convertToAntdSortOrder(queryParam.dateSorting)}
            render={formatDate}
          />
          <Column title="Tag ID" dataIndex="id" className="columnDivider" />
          {isSuperAdmin && (
            <Column
              align="center"
              className="columnDivider"
              render={(tag) => (
                <EditOutlined onClick={() => setSelectedTag(tag)} />
              )}
              width={48}
            />
          )}
        </Table>
      </Content>
      {!!selectedTag && (
        <CustomTagModal
          selectedTag={selectedTag}
          customTags={customTags}
          closeModal={() => setSelectedTag(undefined)}
        />
      )}
    </Layout>
  );
};
